// @ts-expect-error
import moment from 'moment-timezone/builds/moment-timezone-with-data-1970-2030'

import rawlang, * as l10n from '../../../../utils/l10n'
import { ModelPortfolio } from '../../../../types/ModelPortfolio'

const lang = rawlang as 'en' | 'it'

export const RISK_LEVELS = [1, 2, 3, 4, 5, 6, 7]

export const DEFAULT_RISK_LEVEL = 4

export const DEFAULT_COMPLEXITY_LEVEL = 1

export const TIME_HORIZON_OPTIONS = [5, 10, 15, 20, 30].map((x) => ({
  label: `${x} ${l10n.expected_performance.years[lang]}`,
  value: x,
}))

export const computePortfolioProjection = (
  ibbotsonData: ModelPortfolio['expectedPerformance'],
  projectionYears: number,
  initialContribution: number,
  monthlyContribution: number
) => {
  const { mean, perc0025, perc0275, perc0725, perc0975 } = ibbotsonData

  const projMean = computeProjectionForSeries(
    mean,
    initialContribution,
    monthlyContribution
  ).slice(0, projectionYears + 1)

  const proj0025 = computeProjectionForSeries(
    perc0025,
    initialContribution,
    monthlyContribution
  ).slice(0, projectionYears + 1)

  const proj0275 = computeProjectionForSeries(
    perc0275,
    initialContribution,
    monthlyContribution
  ).slice(0, projectionYears + 1)

  const proj0725 = computeProjectionForSeries(
    perc0725,
    initialContribution,
    monthlyContribution
  ).slice(0, projectionYears + 1)

  const proj0975 = computeProjectionForSeries(
    perc0975,
    initialContribution,
    monthlyContribution
  ).slice(0, projectionYears + 1)

  return { projMean, proj0025, proj0275, proj0725, proj0975 }
}

const computeProjectionForSeries = (
  dataset: ModelPortfolio['expectedPerformance']['mean'],
  initialContribution: number,
  monthlyContribution: number
) => {
  const calculatedValues: number[] = []

  dataset.reduce((acc, curr, index) => {
    if (index > 0) {
      const rateOfReturn = curr.expectedValue / dataset[index - 1].expectedValue
      let monthlyRateOfReturn = 0

      for (let i = 1; i < 13; i += 1)
        monthlyRateOfReturn += rateOfReturn ** (i / 12)

      const marketValueEOY =
        acc * rateOfReturn + monthlyContribution * monthlyRateOfReturn

      calculatedValues.push(marketValueEOY)

      return marketValueEOY
    }
    calculatedValues.push(acc)

    return acc
  }, initialContribution)

  return calculatedValues
}

export function getChartDataSet(
  projectionData: Record<string, any>,
  initialContribution: number,
  monthlyContribution: number
) {
  const { projMean, proj0025, proj0275, proj0725, proj0975 } = projectionData
  const currentYear = new Date().getFullYear()
  const xAxis = Array.from(
    projMean,
    (_, index) => new Date(index + currentYear, 0)
  )

  const totalContribution = {
    id: 'totalContribution',
    data: xAxis.map((x, index) => ({
      x: moment(x).format('YYYY-MM-DD'),
      y: initialContribution + monthlyContribution * 12 * index,
    })),
  }

  const expectedValue = {
    id: 'mean',
    data: xAxis.map((x, index) => ({
      x: moment(x).format('YYYY-MM-DD'),
      y: projMean[index],
      lowConfidenceYLow: proj0025[index],
      lowConfidenceYHigh: proj0975[index],
      highConfidenceYLow: proj0275[index],
      highConfidenceYHigh: proj0725[index],
    })),
  }

  return { totalContribution, expectedValue }
}

export function getFormErrors(
  initialContribution: number,
  monthlyContribution: number
): {
  initialContribution?: string
  monthlyContribution?: string
} {
  if (process.env.GATSBY_COUNTRY === 'GB') {
    if (initialContribution < 500)
      return {
        initialContribution:
          l10n.expected_performance.minimumOneTimePersonalContributionAmount[
            lang
          ],
      }

    if (
      initialContribution >= 500 &&
      !!monthlyContribution &&
      monthlyContribution < 100
    )
      return {
        monthlyContribution:
          l10n.expected_performance.minimumMonthlyPersonalContributionAmount[
            lang
          ],
      }
  }

  if (process.env.GATSBY_COUNTRY === 'IT') {
    if (initialContribution < 5000)
      return {
        initialContribution:
          l10n.expected_performance.minimumOneTimePersonalContributionAmount[
            lang
          ],
      }

    if (
      initialContribution >= 5000 &&
      !!monthlyContribution &&
      monthlyContribution < 100
    )
      return {
        monthlyContribution:
          l10n.expected_performance.minimumMonthlyPersonalContributionAmount[
            lang
          ],
      }
  }

  return {}
}

export function getModelPortfoliosJsonUrl() {
  return `${
    process.env.GATSBY_ASSETS_CDN
  }/model-portfolios/${process.env.GATSBY_COUNTRY!.toLowerCase()}.json`
}
